import { Injectable } from "@angular/core";
import { catchError, map, Observable, of, throwError } from "rxjs";

import { HttpClientHelperService } from "src/app/api/http-client-helper.service";
import { BaseApiClientAssessmentService } from "src/app/api/base-api-client-assessment.service";
import { AssessmentAgentResponse, AssessmentAttachment, AssessmentCommentRequest, AssessmentCommentResponse, AssessmentEditRequest, AssessmentLockStatus, AssessmentPageRequest, AssessmentPageResponse, AssessmentQuestionRequest, AssessmentQuestionResponse, AssessmentResourceId, AssessmentSectionRequest, AssessmentSectionResponse } from "src/app/api/models/assessments/assessment.interface";
import { CustomFieldsQuery } from "src/app/company-settings/state/custom-fields/custom-fields.query";
import { CustomValuesQuery } from "src/app/company-settings/state/custom-values/custom-values.query";
import { AssessmentsTemplate } from "src/app/api/models/assessments/assessment-template.interface";
import { AssessmentsTemplateParserService } from "src/app/api/assessments-template-parser.service";
import { RandomUUIDPipe } from "src/app/shared/pipes/random-uuid.pipe";
import { AssessmentEditOpEnum } from "src/app/api/models/assessments/assessment.enum";
import { CollaboratorInviteRequest } from "src/app/collaboration/assessments/models/collaborator-invite-request.interface";

@Injectable({
  providedIn: 'root'
})
export class ApiClientCollaborationAssessmentsService extends BaseApiClientAssessmentService {
  protected readonly instancesController = 'collaboration/assessments';
  protected readonly collaorationControllerV4 = 'assessment/v4/collaboration/assessment-page';

  constructor(
    httpClientHelper: HttpClientHelperService,
    customFieldsQuery: CustomFieldsQuery,
    customValuesQuery: CustomValuesQuery,
    assessmentsTemplateParserService: AssessmentsTemplateParserService,
    randomUUIDPipe: RandomUUIDPipe,
  ) {
    super(httpClientHelper, customFieldsQuery, customValuesQuery, assessmentsTemplateParserService, randomUUIDPipe);
  }

  getAgents(): Observable<AssessmentAgentResponse[]> {
    return this.httpClient.invokeGetAuth<AssessmentAgentResponse[]>(`${this.collaorationControllerV4}/agents`);  
  }

    // Assessment Page
  override getAssessments(): Observable<AssessmentPageResponse[]> {
    return this.httpClient.invokeGetAuth<AssessmentPageResponse[]>(`${this.collaorationControllerV4}`);  
  }

  override getAssessment(assessmentPageId: AssessmentResourceId): Observable<AssessmentPageResponse> {
    return this.httpClient.invokeGetAuth<AssessmentPageResponse>(`${this.collaorationControllerV4}/${assessmentPageId}`);
  }

  override updateAssessment(assessmentPageId: AssessmentResourceId, request: AssessmentEditRequest[]): Observable<AssessmentPageResponse> {
    return this.httpClient.invokePatchAuth<AssessmentPageResponse>(`${this.collaorationControllerV4}/${assessmentPageId}`, request);
  }

  override deleteAssessment(assessmentPageId: AssessmentResourceId): Observable<void> {
    return this.httpClient.invokeDeleteAuth<void>(`${this.collaorationControllerV4}/${assessmentPageId}`);
  }

  override createAssessment(request: AssessmentPageRequest): Observable<AssessmentPageResponse> {
    return this.httpClient.invokePostAuth<AssessmentPageResponse>(`${this.collaorationControllerV4}`, request);
  }

  /**
   * Collaborators inviting collaborators (DevCollabInviteCollab) — BE handoff: one email per call, note ≤ 2000 chars.
   * No `visibilityScope` is ever sent (the invitee gets the caller's scope server-side), so the base signature's
   * scope argument is ignored on purpose. 200 = the page as the collaborator sees it (`CollabPageResponse`);
   * an already-active person or a self-invite is a 200 no-op; 400 messages are user-facing; 403 = caller not
   * active or flag off.
   */
  override inviteCollaborator(assessmentPageId: AssessmentResourceId, email: string, message?: string): Observable<AssessmentPageResponse> {
    const payload: CollaboratorInviteRequest = { email };
    if (message) {
      payload.inviteMessage = message;
    }
    return this.httpClient.invokePostAuth<AssessmentPageResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/collaborator`, payload);
  }

  /**
   * Collaborator removes someone they invited (BE handoff §2, PR #43432): `{ email }` exactly as invited. 200 = the page
   * as the collaborator sees it; 403 unless the caller is that invite's `invitedBy` (incl. self); 404 unknown email;
   * already revoked = 200 no-op; no cascade. Same flag as the invite.
   */
  override revokeCollaborator(assessmentPageId: AssessmentResourceId, email: string): Observable<AssessmentPageResponse> {
    return this.httpClient.invokeDeleteAuth<AssessmentPageResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/collaborator`, { email });
  }

  // Assessment Section
  override getAssessmentSections(assessmentPageId: AssessmentResourceId): Observable<AssessmentSectionResponse> {
    return this.httpClient.invokeGetAuth<AssessmentSectionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section`);
  }

  override getAssessmentSection(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId): Observable<AssessmentSectionResponse> {
    return this.httpClient.invokeGetAuth<AssessmentSectionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}`);   
  }

  override updateAssessmentSection(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, request: AssessmentEditRequest[]): Observable<AssessmentSectionResponse> {
    return this.httpClient.invokePatchAuth<AssessmentSectionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}`, request);
  }

  override deleteAssessmentSection(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId): Observable<AssessmentSectionResponse> {
    return this.httpClient.invokeDeleteAuth<AssessmentSectionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}`);
  }

  override createAssessmentSection(assessmentPageId: AssessmentResourceId, request: AssessmentSectionRequest): Observable<AssessmentSectionResponse> {
    return this.httpClient.invokePostAuth<AssessmentSectionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section`, request);
  }

  // Assessment Question
  override getAssessmentQuestions(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId): Observable<AssessmentQuestionResponse[]> {
    return this.httpClient.invokeGetAuth<AssessmentQuestionResponse[]>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}/question`);
  }

  override getAssessmentQuestion(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId): Observable<AssessmentQuestionResponse> {
    return this.httpClient.invokeGetAuth<AssessmentQuestionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}`);
  }

  override updateAssessmentQuestion(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId, request: AssessmentEditRequest[]): 
    Observable<AssessmentQuestionResponse> {
    return this.httpClient.invokePatchAuth<AssessmentQuestionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}`, request);
  }

  override deleteAssessmentQuestion(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, questionId: AssessmentResourceId): Observable<AssessmentQuestionResponse> {
    return this.httpClient.invokeDeleteAuth<AssessmentQuestionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}`);
  }

  override createAssessmentQuestion(assessmentPageId: AssessmentResourceId, sectionId: AssessmentResourceId, request: AssessmentQuestionRequest): Observable<AssessmentQuestionResponse> {
    return this.httpClient.invokePostAuth<AssessmentQuestionResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}/question`, request);
  }

  /**
   * @param notifyOwner When omitted or `undefined`, the `notifyOwner` field is not sent (legacy mark-done without notification preference).
   */
  markDone(
    assessmentPageId: AssessmentResourceId,
    isDone: boolean,
    notifyOwner?: boolean,
    markForAll?: boolean,
  ): Observable<AssessmentPageResponse> {
    const body: { isDone: boolean; notifyOwner?: boolean; markForAll?: boolean } = { isDone };
    if (notifyOwner !== undefined) {
      body.notifyOwner = notifyOwner;
    }
    if (markForAll !== undefined) {
      body.markForAll = markForAll;
    }
    return this.httpClient.invokePostAuth<AssessmentPageResponse>(
      `${this.collaorationControllerV4}/${assessmentPageId}/mark-done`,
      body,
    );
  }

  override getLockStatus(instanceId: AssessmentResourceId): Observable<AssessmentLockStatus> {
    return this.httpClient.invokeGetAuth<AssessmentLockStatus>(`${this.collaorationControllerV4}/${instanceId}/lock-status`);
  }

  override setLockStatus(instanceId: AssessmentResourceId, isLocked: boolean): Observable<AssessmentLockStatus> {
    return this.httpClient.invokePost<AssessmentLockStatus>(`${this.collaorationControllerV4}/${instanceId}/lock-status`, {
        isLocked
    });
  }

  override submitInstance(assessmentPageId: AssessmentResourceId): Observable<AssessmentPageResponse> {
    try {  
      return this.httpClient.invokePostAuth<AssessmentPageResponse>(
        `${this.collaorationControllerV4}/${assessmentPageId}/submit`
      ).pipe(
        map(() => ({} as AssessmentPageResponse)),
        catchError(error => throwError(() => error))
      );
    } catch (parseError) {
      return throwError(() => parseError);
    }
  }

  override uploadInstanceAttachment(instanceId: AssessmentResourceId, file: File): Observable<AssessmentAttachment> {
    return this.httpClient.invokeUploadFileAuth<AssessmentAttachment>(`${this.instancesController}/${instanceId}/attachment`, file);
  }

  override getInstanceAttachmentUrl(instanceId: AssessmentResourceId, attachmentId: string): Observable<string> {
      return this.httpClient.invokeGetAuth<string>(`${this.instancesController}/${instanceId}/attachment/${attachmentId}`);
  }

  override getTemplates(): Observable<AssessmentsTemplate[]> {
    return of([]);
  }

  override getAssessmentComments(assessmentPageId: AssessmentResourceId): Observable<AssessmentCommentResponse[]> {
    return this.httpClient.invokeGetAuth<AssessmentCommentResponse[]>(`${this.collaorationControllerV4}/${assessmentPageId}/notes`);
  }

  override createComment(
    assessmentPageId: AssessmentResourceId, 
    sectionId: AssessmentResourceId, 
    questionId: AssessmentResourceId, 
    comment: AssessmentCommentRequest
  ): Observable<AssessmentCommentResponse> {
    return this.httpClient.invokePostAuth<AssessmentCommentResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}/notes`, comment);
  }

  override resolveComment(
    assessmentPageId: AssessmentResourceId, 
    sectionId: AssessmentResourceId, 
    questionId: AssessmentResourceId, 
    commentId: AssessmentResourceId, 
    isResolved: boolean
  ): Observable<AssessmentCommentResponse> {
    return this.httpClient.invokePatchAuth<AssessmentCommentResponse>(`${this.collaorationControllerV4}/${assessmentPageId}/section/${sectionId}/question/${questionId}/notes/${commentId}`, [{
      op: AssessmentEditOpEnum.Replace,
      path: `isResolved`,
      value: isResolved
    }]);
  }
}
